import { HelpRequestButton } from 'components/help-request-button';
import { FormattedMessage } from 'components/shared/Intl';
import { FC } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { ApplyStatus, ProductTradingOption, ProductTradingOptions } from 'state/tradeAllocationForm';

import { TradeAllocationSecondaryRedirectButton } from './TradeAllocationSecondaryRedirectButton';

type OwnProps = {
  onApply: () => void;
  onReload: () => void;
  openInSecondaryMarketQuote: (option: ProductTradingOption) => void;
  openInAddOn: () => void;
  isReadyToApply: boolean;
  applyStatus: ApplyStatus;
  isReadOnly: boolean;
  canApply: boolean;
  isTradeWithMonetaryScheduleNotStarted: boolean;
  isTradeWithMonetaryScheduleClosed: boolean;
  isMonetarySchedulePriceUnavailable: boolean;
  productTradingFormOptions?: ProductTradingOptions;
};

export const TradeAllocationButtons: FC<OwnProps> = ({
  onApply,
  onReload,
  isReadyToApply,
  applyStatus,
  isReadOnly,
  canApply,
  isTradeWithMonetaryScheduleNotStarted,
  isTradeWithMonetaryScheduleClosed,
  isMonetarySchedulePriceUnavailable,
  productTradingFormOptions,
  openInSecondaryMarketQuote,
  openInAddOn,
}) => {
  const shouldShowApplyButton =
    applyStatus === 'Initialized' &&
    !isReadOnly &&
    !isTradeWithMonetaryScheduleNotStarted &&
    !isTradeWithMonetaryScheduleClosed;

  const shouldShowResetButton =
    applyStatus === 'Initialized' &&
    !isReadOnly &&
    !isTradeWithMonetaryScheduleNotStarted &&
    !isTradeWithMonetaryScheduleClosed &&
    !isMonetarySchedulePriceUnavailable;

  const shouldShowOkButton =
    (applyStatus === 'Done' || applyStatus === 'Error') &&
    !isTradeWithMonetaryScheduleClosed &&
    !isTradeWithMonetaryScheduleNotStarted;

  function getAvailableTradingOptions(
    productTradingOptions?: ProductTradingOptions,
  ): { label: string; onClick: () => void }[] {
    if (!productTradingOptions) {
      return [];
    }

    const allOptions: { option: ProductTradingOption; label: string }[] = [
      { option: 'ebiz', label: 'Quote' },
      { option: 'cpOrder', label: 'CP order' },
      { option: 'liveOrder', label: 'Live quote' },
      { option: 'limitOrder', label: 'Limit order' },
    ];

    return allOptions
      .filter(({ option }) => productTradingOptions[option])
      .map(({ option, label }) => ({ label, onClick: () => openInSecondaryMarketQuote(option) }));
  }

  return (
    <div
      className="d-flex flex-column flex-grow-1 justify-content-end sticky-bottom m-3 action-buttons"
      data-e2e="product-modification-actions"
    >
      {isMonetarySchedulePriceUnavailable && (
        <HelpRequestButton className="btn btn-xl btn-discreet-secondary m-1 mx-0">
          <FormattedMessage id="tradeAllocation.button.helpRequest" />
          <em className="icon icon-outlined ms-1">live_help</em>
        </HelpRequestButton>
      )}
      {shouldShowApplyButton && (
        <button
          data-e2e="apply-button"
          disabled={!isReadyToApply || !canApply}
          className="btn btn-xl btn-info my-1 mx-0"
          onClick={onApply}
        >
          <FormattedMessage id="tradeAllocation.button.apply" defaultMessage="Apply" />
        </button>
      )}
      {shouldShowResetButton && (
        <button
          data-e2e="reset-button"
          className="btn btn-xl btn-primary my-1 mx-0"
          onClick={() => {
            logAnalytics.trackEvent('Trade allocation', 'Reset');
            onReload();
          }}
        >
          <FormattedMessage id="tradeAllocation.button.reset" defaultMessage="Reset" />
        </button>
      )}
      {shouldShowOkButton && (
        <button
          data-e2e="ok-button"
          className="btn btn-xl btn-primary m-1"
          onClick={() => {
            logAnalytics.trackEvent('Trade allocation', 'Ok');
            onReload();
          }}
        >
          <FormattedMessage id="tradeAllocation.button.ok" defaultMessage="Ok" />
        </button>
      )}
      {isTradeWithMonetaryScheduleNotStarted && (
        <button
          className="btn btn-xl btn-primary m-1 mx-0"
          onClick={() => {
            logAnalytics.trackEvent('Trade allocation', 'Open in Add-on');
            openInAddOn();
          }}
        >
          <FormattedMessage id="tradeAllocation.button.redirect.AddOn" />
          <em className="icon ms-1">open_in_new</em>
        </button>
      )}
      {isTradeWithMonetaryScheduleClosed &&
        (() => {
          const availableTradingOptions = getAvailableTradingOptions(productTradingFormOptions);
          return (
            <TradeAllocationSecondaryRedirectButton
              label={<FormattedMessage id="tradeAllocation.button.redirect.secondaryMarketPostTrade" />}
              isDisabled={availableTradingOptions.length === 0}
              secondaryActions={availableTradingOptions}
            />
          );
        })()}
    </div>
  );
};

TradeAllocationButtons.displayName = 'TradeAllocationButtons';
